import React, { useMemo } from 'react';
import { Box, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Autocomplete, CircularProgress } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { fetchAllShifts } from 'core/API/shifts';
import { getUsersByWorkplace } from 'core/API/checklist';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import moment from 'moment-timezone';

interface TradeShiftFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	currentWeekStart?: Date;
	currentWeekEnd?: Date;
	formRef?: React.MutableRefObject<any>;
}

const validationSchema = Yup.object({
	myShiftId: Yup.string().required('Please select your shift to trade'),
	tradeWithEmployeeId: Yup.string().required('Please select an employee to trade with'),
	theirShiftId: Yup.string().required('Please select the shift to trade for'),
	notes: Yup.string(),
});

const TradeShiftForm: React.FC<TradeShiftFormProps> = ({ 
	onSubmit, 
	onCancel: _onCancel, 
	currentWeekStart, 
	currentWeekEnd,
	formRef
}) => {
	const { workplaceId, timezones } = useWorkplacesContext();
	const currentUserId = localStorage.getItem('userId');
	// Fetch user's shifts for trade request
	const { data: shiftsData, isLoading: shiftsLoading } = useQuery(
		['user-shifts', workplaceId, currentUserId, currentWeekStart, currentWeekEnd],
		() =>
			fetchAllShifts({
				workplaceId,
				fromDate: currentWeekStart ? moment(currentWeekStart).format('YYYY-MM-DD') : undefined,
				toDate: currentWeekEnd ? moment(currentWeekEnd).format('YYYY-MM-DD') : undefined,
			}),
		{
			enabled: !!workplaceId && !!currentWeekStart && !!currentWeekEnd && !!currentUserId,
			staleTime: 0,
			cacheTime: 0,
		}
	);

	// Fetch employees for trade selection
	const { data: employeesData, isLoading: employeesLoading } = useQuery(
		['users-workplaces'],
		() => getUsersByWorkplace(),
		{
			enabled: true,
		}
	);

	// Filter user's shifts for the selected week
	const userShifts = useMemo(() => {
		if (!shiftsData?.member || !currentUserId) return [];
		const weekStart = moment(currentWeekStart).startOf('day');
		const weekEnd = moment(currentWeekEnd).endOf('day');
		const currentUserIdStr = currentUserId.toString();
		
		return shiftsData.member.filter((shift: any) => {
			// Extract employee ID from shift - handle multiple formats
			let employeeId = '';
			if (shift.employee?.id) {
				employeeId = shift.employee.id.toString();
			} else if (typeof shift.employee === 'string') {
				// Handle case where employee is a URL reference
				employeeId = shift.employee.replace('/api/employees/', '');
			}
			
			const shiftStart = moment(shift.startAt || shift.start);
			
			// Only include published shifts for current user that fall within the selected week
			return employeeId === currentUserIdStr && 
				   shiftStart.isBetween(weekStart, weekEnd, null, '[]') &&
				   shift.published === true;
		});
	}, [shiftsData, currentUserId, currentWeekStart, currentWeekEnd]);

	// Filter out current user from employees list
	const otherEmployees = useMemo(() => {
		if (!employeesData || !currentUserId) return [];
		return employeesData.filter((emp: any) => emp.id.toString() !== currentUserId);
	}, [employeesData, currentUserId]);

	// Sort otherEmployees by name
	const sortedOtherEmployees = useMemo(() => {
		return [...otherEmployees].sort((a, b) => {
			const aName = `${a.firstName} ${a.lastName}`;
			const bName = `${b.firstName} ${b.lastName}`;
			return aName.localeCompare(bName);
		});
	}, [otherEmployees]);

	const initialValues = {
		myShiftId: '',
		tradeWithEmployeeId: '',
		theirShiftId: '',
		notes: '',
	};

	return (
		<Formik
			innerRef={formRef}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={onSubmit}
		>
			{({ values, setFieldValue, errors, touched }) => {
				// Fetch the selected employee's shifts for the selected week
				const { data: selectedEmployeeShiftsData, isLoading: selectedEmployeeShiftsLoading } = useQuery(
					['employee-shifts', workplaceId, values.tradeWithEmployeeId, currentWeekStart, currentWeekEnd],
					() =>
						fetchAllShifts({
							workplaceId,
							fromDate: currentWeekStart ? moment(currentWeekStart).format('YYYY-MM-DD') : undefined,
							toDate: currentWeekEnd ? moment(currentWeekEnd).format('YYYY-MM-DD') : undefined,
						}),
					{
						enabled: !!workplaceId && !!currentWeekStart && !!currentWeekEnd && !!values.tradeWithEmployeeId,
						staleTime: 0,
						cacheTime: 0,
					}
				);

				// Filter selected employee's shifts for the selected week
				const selectedEmployeeShifts = useMemo(() => {
					if (!selectedEmployeeShiftsData?.member || !values.tradeWithEmployeeId) return [];
					const weekStart = moment(currentWeekStart).startOf('day');
					const weekEnd = moment(currentWeekEnd).endOf('day');
					const selectedEmployeeIdStr = values.tradeWithEmployeeId.toString();
					
					return selectedEmployeeShiftsData.member.filter((shift: any) => {
						// Extract employee ID from shift - handle multiple formats
						let employeeId = '';
						if (shift.employee?.id) {
							employeeId = shift.employee.id.toString();
						} else if (typeof shift.employee === 'string') {
							// Handle case where employee is a URL reference
							employeeId = shift.employee.replace('/api/employees/', '');
						}
						
						const shiftStart = moment(shift.startAt || shift.start);
						
						// Only include shifts for the selected employee that fall within the selected week
						return employeeId === selectedEmployeeIdStr &&
							   shiftStart.isBetween(weekStart, weekEnd, null, '[]');
					});
				}, [selectedEmployeeShiftsData, values.tradeWithEmployeeId, currentWeekStart, currentWeekEnd]);

				return (
					<Form>
						<Box sx={{ mt: 3 }}>
							<Typography variant="h6" gutterBottom>
								Trade Shift Request
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
								Week: {currentWeekStart && moment(currentWeekStart).format('MMM D')} - {currentWeekEnd && moment(currentWeekEnd).format('MMM D, YYYY')}
							</Typography>

							<Grid container spacing={3}>
							{/* My Shift Selection */}
							<Grid item xs={12}>
								<FormControl fullWidth error={touched.myShiftId && !!errors.myShiftId}>
									<InputLabel>Select Your Shift to Trade *</InputLabel>
									<Select
										value={values.myShiftId}
										onChange={(e) => setFieldValue('myShiftId', e.target.value)}
										label="Select Your Shift to Trade *"
									>
										{shiftsLoading ? (
											<MenuItem value="" disabled>
												<CircularProgress size={20} color="inherit" /> Loading shifts...
											</MenuItem>
										) : userShifts.length === 0 ? (
											<MenuItem value="" disabled>No shifts available to trade</MenuItem>
										) : (
											userShifts.map((shift: any) => (
												<MenuItem key={shift.id} value={shift.id}>
													{moment(`${shift.startAt}.000Z`).format('ddd, MMM D - HH:mm')} - {moment(`${shift.endAt}.000Z`).format('HH:mm')} | {shift.jobCode?.label || 'Shift'}
												</MenuItem>
											))
										)}
									</Select>
									{touched.myShiftId && errors.myShiftId && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
											{errors.myShiftId}
										</Typography>
									)}
								</FormControl>
							</Grid>

							{/* Employee to Trade With */}
							<Grid item xs={12}>
								<Autocomplete
									options={sortedOtherEmployees}
									getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''}`.trim() || option.email}
									value={
										values.tradeWithEmployeeId
											? (sortedOtherEmployees.find((emp: any) => emp.id.toString() === values.tradeWithEmployeeId) || null)
											: null
									}
									onChange={(_, value) => {
										setFieldValue('tradeWithEmployeeId', value ? value.id : '');
										// Reset their shift selection when employee changes
										setFieldValue('theirShiftId', '');
									}}
									isOptionEqualToValue={(option, value) => option.id.toString() === value?.id.toString()}
									loading={employeesLoading}
									fullWidth
									renderInput={(params) => (
										<TextField
											{...params}
											label="Trade With Employee *"
											error={touched.tradeWithEmployeeId && !!errors.tradeWithEmployeeId}
											helperText={touched.tradeWithEmployeeId && errors.tradeWithEmployeeId}
										/>
									)}
									noOptionsText={sortedOtherEmployees.length === 0 ? 'No other employees available' : 'No options'}
								/>
							</Grid>

							{/* Their Shift Selection */}
							<Grid item xs={12}>
								<FormControl 
									fullWidth 
									error={touched.theirShiftId && !!errors.theirShiftId}
									disabled={!values.tradeWithEmployeeId}
								>
									<InputLabel>Select Their Shift to Trade For *</InputLabel>
									<Select
										value={values.theirShiftId}
										onChange={(e) => setFieldValue('theirShiftId', e.target.value)}
										label="Select Their Shift to Trade For *"
										disabled={!values.tradeWithEmployeeId}
									>
										{!values.tradeWithEmployeeId ? (
											<MenuItem value="" disabled>Please select an employee first</MenuItem>
										) : selectedEmployeeShiftsLoading ? (
											<MenuItem value="" disabled>
												<CircularProgress size={20} color="inherit" /> Loading shifts...
											</MenuItem>
										) : selectedEmployeeShifts.length === 0 ? (
											<MenuItem value="" disabled>Selected employee has no available shifts</MenuItem>
										) : (
											selectedEmployeeShifts.map((shift: any) => (
												<MenuItem key={shift.id} value={shift.id}>
													{moment(`${shift.startAt}.000Z`).format('ddd, MMM D - HH:mm')} - {moment(`${shift.endAt}.000Z`).format('HH:mm')} | {shift.jobCode?.label || 'Shift'}
												</MenuItem>
											))
										)}
									</Select>
									{touched.theirShiftId && errors.theirShiftId && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
											{errors.theirShiftId}
										</Typography>
									)}
								</FormControl>
							</Grid>

							{/* Notes/Reason */}
							<Grid item xs={12}>
								<TextField
									fullWidth
									multiline
									rows={3}
									label="Notes (Optional)"
									placeholder="Add any additional notes or reason for the trade request..."
									value={values.notes}
									onChange={(e) => setFieldValue('notes', e.target.value)}
									error={touched.notes && !!errors.notes}
									helperText={touched.notes && errors.notes}
								/>
							</Grid>
						</Grid>
					</Box>
				</Form>
				);
			}}
		</Formik>
	);
};

export default TradeShiftForm;
